import { ArrowRight, Check, Flag, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { player } from '../audio/player';
import { RoleChip } from '../components/Cast';
import { FlagSheet } from '../components/FlagSheet';
import { Illustration } from '../components/Illustration';
import { StepHead } from '../components/StepHead';
import { GreekText } from '../components/GreekText';
import { PlayButton } from '../components/PlayButton';
import { course, staffOf } from '../content';
import { isStoryItem, storyItems } from '../stories/words';
import type { Item } from '../content/types';
import { looseText } from '../lib/greek';
import { hearOptions } from '../lib/options';
import { isMet } from '../lib/plan';
import type { CardRecord } from '../lib/scheduler';
import { clipFor, itemVoice, speechRange, VOICE_LABEL } from '../lib/voice';
import { grade, presented, useAppState } from '../state/store';

interface Props {
  card: CardRecord;
  item: Item;
  mode: 'first' | 'review' | 'repeat';
  onDone: () => void;
  onSkip: () => void;
  onFlagged: () => void;
}

/**
 * The hear-it card: the phrase is played (no text), and the learner picks its meaning from four.
 * The wrong options are look-alikes from the same pattern or lesson, so the small words matter.
 */
export function HearStep({ card, item, mode, onDone, onSkip, onFlagged }: Props) {
  const state = useAppState();
  const { settings } = state;
  const voice = itemVoice(item, staffOf(item));
  const clip = clipFor(item, voice);
  const range = useMemo(() => speechRange(clip), [clip]);
  // A story phrase is told apart from the other story phrases; a course phrase from course phrases.
  const pool = isStoryItem(item.id) ? { ...course, items: storyItems } : course;
  const options = useMemo(() => hearOptions(item, pool, Math.random, (id) => isMet(state, id)), [item, card.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The choices appear once the phrase has been heard, so the learner listens rather than reads
  // ahead. If the first play can't start on its own, they appear after the clip's length anyway.
  const [heard, setHeard] = useState(false);
  const [pick, setPick] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const continueRef = useRef<HTMLButtonElement>(null);
  const tag = `hear:${card.id}`;

  const play = () => {
    setNeedsTap(false);
    player.play(clip.src, { ...range, tag }).then((r) => {
      if (r === 'error') setBroken(true);
      if (r === 'ended') setHeard(true);
    });
  };

  useEffect(() => {
    presented(card.id);
    // Played as the card appears, right after the tap that brought it here.
    const t = window.setTimeout(() => {
      player.play(clip.src, { ...range, tag }).then((r) => {
        if (r === 'blocked') setNeedsTap(true);
        if (r === 'error') setBroken(true);
        if (r === 'ended') setHeard(true);
      });
    }, 250);
    const fallback = window.setTimeout(() => setHeard(true), 250 + (range.to - range.from) * 1000 + 700);
    return () => {
      clearTimeout(t);
      clearTimeout(fallback);
      player.stop();
    };
  }, [card.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (meaning: string) => {
    if (pick || busy) return;
    setBusy(true);
    setPick(meaning);
    const right = meaning === item.meaning;
    await grade(card.id, right ? 'right' : 'wrong', { repeat: mode === 'repeat', learning: mode === 'first', pick: meaning });
    setBusy(false);
    // Hear it again with the words and meaning in view.
    player.play(clip.src, { ...range, tag });
    requestAnimationFrame(() => continueRef.current?.focus({ preventScroll: true }));
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if ((e.target as HTMLElement)?.closest('input, textarea, dialog[open]')) return;
      const n = Number(e.key);
      if (!pick && heard && n >= 1 && n <= options.length) {
        e.preventDefault();
        choose(options[n - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (broken) {
    return (
      <div className="step">
        <div className="banner" role="alert">
          <X size={18} aria-hidden="true" />
          <p>This card's audio can't be played, so it's skipped for today.</p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={onSkip}>
            Continue <ArrowRight size={20} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  const right = pick === item.meaning;
  return (
    <div className="step">
      <div className="stack">
        <StepHead
          kind="understand"
          role={
            // kept in place before the answer (hidden), so nothing shifts as you tap
            <span style={{ visibility: pick ? 'visible' : 'hidden' }}>
              <RoleChip staff={staffOf(item)} from={item.role === 'hear' ? 'staff' : 'you'} />
            </span>
          }
          why={state.profile.lessonsDone.length === 0 && mode === 'first'}
        >
          {mode === 'repeat' && <span className="chip chip-warn">One more time</span>}
        </StepHead>
        <h2 className="english-cue" id={`q-${card.id}`}>
          What does it mean?
        </h2>
      </div>

      <div className="row" style={{ gap: 18, justifyContent: "center" }}>
        <PlayButton src={clip.src} tag={tag} label="Play again" ariaLabel={`Play the phrase, ${VOICE_LABEL[voice].toLowerCase()}'s voice`} from={range.from} to={range.to} variant="big" onResult={(r) => r === 'error' && setBroken(true)} />
        <div className="stack" style={{ gap: 6 }}>
          <PlayButton src={clip.src} tag={`${tag}:slow`} label="Slowly" rate={0.75} from={range.from} to={range.to} />
          {needsTap && !pick && (
            <button type="button" className="btn btn-ghost small" onClick={play}>
              Tap to listen
            </button>
          )}
        </div>
      </div>

      {heard ? (
        <div className="options" role="group" aria-labelledby={`q-${card.id}`} data-answered={pick ? true : undefined}>
          {options.map((meaning, i) => {
            const result = pick ? (meaning === item.meaning ? 'right' : meaning === pick ? 'wrong' : undefined) : undefined;
            return (
              <button
                key={meaning}
                type="button"
                className="option"
                data-result={result}
                disabled={Boolean(pick)}
                onClick={() => choose(meaning)}
                aria-describedby={result ? `r-${card.id}` : undefined}
                aria-keyshortcuts={String(i + 1)}
              >
                {result && <span className="option-key" aria-hidden="true">{result === 'right' ? <Check size={18} /> : <X size={18} />}</span>}
                <span>{meaning}</span>
                {result && <span className="sr-only">{result === 'right' ? '(the right answer)' : '(your pick)'}</span>}
              </button>
            );
          })}
        </div>
      ) : (
        // Where the choices will be: the same space, nothing to read ahead.
        <div className="options" aria-hidden="true">
          {options.map((meaning) => (
            <div key={meaning} className="option-ghost" />
          ))}
        </div>
      )}

      <div className="actions">
        {pick ? (
          <div className="answer-sheet">
            <div className="answer-sheet-head">
              <p id={`r-${card.id}`} className={`result ${right ? 'result-right' : 'result-wrong'}`} role="status">
                {right ? <Check size={22} aria-hidden="true" /> : <X size={22} aria-hidden="true" />}
                {right ? 'Right' : 'Not quite. Listen once more.'}
              </p>
              <button type="button" className="icon-btn icon-btn-plain" onClick={() => setFlagOpen(true)} aria-label="Flag this phrase as wrong">
                <Flag size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="answer-sheet-body">
              <div className="answer-side">
                <Illustration id={item.id} size="thumb" decorative className="answer-art" />
                <PlayButton src={clip.src} tag={`${tag}:again`} label="Hear it again" from={range.from} to={range.to} variant="icon" />
              </div>
              <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                {/* The situation would have given the answer away; it comes with it. */}
                {item.context && item.role === 'hear' && <p className="context">{item.context}</p>}
                <GreekText text={item.text} size="large" ipa={item.ipa} showRespelling={settings.showRespelling} />
                <p className="meaning">{item.meaning}</p>
                {looseText(item.gloss) !== looseText(item.meaning) && <p className="gloss small">{item.gloss}</p>}
              </div>
            </div>
            <button ref={continueRef} type="button" className="btn btn-primary btn-block btn-lg" onClick={onDone} disabled={busy}>
              Continue <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
        ) : (
          <p className="small muted" style={{ textAlign: 'center' }}>
            {heard ? 'Pick the meaning. Not sure? Play it slowly.' : 'Listening… the choices appear after.'}
          </p>
        )}
      </div>

      <FlagSheet itemId={item.id} open={flagOpen} onClose={() => setFlagOpen(false)} onFlagged={onFlagged} />
    </div>
  );
}
